import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import type { ClinicInterface } from '../models/clinic.interface';
import { NavPrefDefault } from '../models/navPrefDefault';
import type { Preferences, UserPreferences } from '../models/preferences.interface';

@Injectable({
  providedIn: 'root'
})
export class PreferencesService {
  constructor(private readonly apollo: Apollo) { }

  savePreferences(preferences: Preferences): Observable<UserPreferences | undefined> {
    const preferencesSerialized = JSON.stringify(preferences);

    return this.apollo.mutate<{ changeMyUserPreferencies: UserPreferences }>({
      mutation: gql`mutation changeMyUserPreferencies($preferencesSerialized: String!) {
        changeMyUserPreferencies(preferencies: $preferencesSerialized) {
          preferencies
          id
          selectedClinicId
          selectedClinicName
        }
      }`,
      variables: { preferencesSerialized }
    }).pipe(map(res => {
      if (res.data) {
        if (res.data.changeMyUserPreferencies.preferencies) {
          res.data.changeMyUserPreferencies.preferenciesObject = JSON.parse(res.data.changeMyUserPreferencies.preferencies) as Preferences;
        }

        return res.data.changeMyUserPreferencies;
      }

      return undefined;
    }));
  }

  getPreferences(): Observable<UserPreferences> {
    return this.apollo.watchQuery<{ me: UserPreferences }>({
      query : gql` query getPreferences {
        me {
          id
          firstName
          lastName
          preferencies
          selectedClinicId
          selectedClinicName
          locale
          signature
          securityInfo {
            claims
            roles
            roleIds
          }
        }
      }`
    }).valueChanges.pipe(map(res => {
      const defautPref = new NavPrefDefault();
      const userPref: UserPreferences = {
        id : res.data.me.id,
        preferenciesObject : res.data.me.preferencies ? JSON.parse(res.data.me.preferencies) : defautPref,
        selectedClinicId : res.data.me.selectedClinicId,
        selectedClinicName : res.data.me.selectedClinicName,
        signature: res.data.me.signature,
        securityInfo : {
          claims : res.data.me.securityInfo.claims,
          roles : res.data.me.securityInfo.roles,
          roleIds : res.data.me.securityInfo.roleIds,
        },
        locale: res.data.me.locale,
        firstName: res.data.me.firstName,
        lastName: res.data.me.lastName
      };

      return userPref;
    }));
  }

  updateSelectClinic(clinicId: number): Observable<ClinicInterface | undefined> {
    return this.apollo.mutate<{ updateSelectedClinic: ClinicInterface }>({
      mutation: gql`mutation updateSelectedClinic($clinicId: Long!) {
        updateSelectedClinic(clinicId: $clinicId) {
          id
          name
        }
      }`, variables: { clinicId }
    }).pipe(map(res => {
      if (res.data) {
        return res.data?.updateSelectedClinic;
      }

      return undefined;
    }));
  }
}
